import {
  Alert,
  Button,
  Card,
  Center,
  Code,
  Loader,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { Navigate, Route, Routes } from "react-router-dom";
import { Suspense, lazy } from "react";
import { useAuth } from "./auth/AuthContext";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { AppShell } from "./shell/AppShell";
import { MyWorkProvider } from "./shell/MyWorkContext";
import { ChangePassword } from "./screens/ChangePassword";
import { Home } from "./screens/Home";
import { Login } from "./screens/Login";
import { Profile } from "./screens/Profile";
import { AdminHome } from "./screens/admin/AdminHome";
import { AuditLog } from "./screens/admin/AuditLog";
import { UserForm } from "./screens/admin/UserForm";
import { UserList } from "./screens/admin/UserList";
import { InventoryHome } from "./screens/inventory/InventoryHome";
import { MovementLedger } from "./screens/inventory/MovementLedger";
import { Reconciliation } from "./screens/inventory/Reconciliation";
import { StockIn } from "./screens/inventory/StockIn";
import { StockLevels } from "./screens/inventory/StockLevels";
import { DeliveryHome } from "./screens/delivery/DeliveryHome";
import { DeliveryQueue } from "./screens/delivery/DeliveryQueue";
import { DeliveryTaskDetail } from "./screens/delivery/DeliveryTaskDetail";
import { CustomerAdmin } from "./screens/manager/CustomerAdmin";
import { ProductAdmin } from "./screens/manager/ProductAdmin";
import { Reports } from "./screens/manager/Reports";
import { CollectBattery } from "./screens/service/CollectBattery";
import { ServiceHome } from "./screens/service/ServiceHome";
import { ServiceJobDetail } from "./screens/service/ServiceJobDetail";
import { ServiceQueue } from "./screens/service/ServiceQueue";
import { MissingPhones } from "./screens/manager/MissingPhones";
import { PriceList } from "./screens/manager/PriceList";
import { MarketingHome } from "./screens/marketing/MarketingHome";
import { BillGenerate } from "./screens/sales/BillGenerate";
import { BillList } from "./screens/sales/BillList";
import { OrderConfirm } from "./screens/sales/OrderConfirm";
import { OrderList } from "./screens/sales/OrderList";
import { PreOrderQueue } from "./screens/sales/PreOrderQueue";
import { SalesHome } from "./screens/sales/SalesHome";
import { CollectionsHome } from "./screens/collections/CollectionsHome";
import { OutstandingBills } from "./screens/collections/OutstandingBills";
import { PaymentCollect } from "./screens/collections/PaymentCollect";
import { PaymentsRegister } from "./screens/collections/PaymentsRegister";
import { RecordPayment } from "./screens/collections/RecordPayment";
import { PreOrderList } from "./screens/marketing/PreOrderList";
import { PreOrderPlace } from "./screens/marketing/PreOrderPlace";
import type { Module, Role } from "./lib/api";

/**
 * The primitive gallery is loaded on demand. It imports every component in
 * components/ui at once, which is the point of a gallery and exactly what
 * should not sit in the bundle every signed-in user downloads to reach their
 * own module - statically imported it cost ~24 KB gzipped of the main chunk.
 */
const DevKit = lazy(() =>
  import("./screens/DevKit").then((module) => ({ default: module.DevKit })),
);

/**
 * Route-level gating hides what a role may not use. It is a usability
 * measure only - every API endpoint independently enforces access
 * (REQ-NFR-4), so a hand-typed URL gains nothing.
 */
function Guarded({ module, children }: { module: Module; children: React.ReactNode }) {
  const { can } = useAuth();
  return can(module) ? <>{children}</> : <Navigate to="/" replace />;
}

/** For screens gated on role rather than module - the customer master (Q6). */
function RoleGuarded({
  roles,
  children,
}: {
  roles: readonly Role[];
  children: React.ReactNode;
}) {
  const { user, hasRole } = useAuth();
  // ANY of their roles, not the primary one: the customer master is the Store
  // Manager's, and a Store Manager who also carries deliveries must not be
  // bounced off it because Delivery Agent sorted first.
  return user && hasRole(...roles) ? (
    <>{children}</>
  ) : (
    <Navigate to="/" replace />
  );
}

export function App() {
  const { status, mustChangePassword, errorMessage, refresh, signOut } = useAuth();

  if (status === "loading") {
    return (
      <Center mih="100dvh" bg="gray.0">
        <Loader aria-label="Loading" />
      </Center>
    );
  }

  // The server could not be asked who we are. Shown instead of silently
  // bouncing to the login screen, which is what used to happen and made a
  // server-side fault indistinguishable from a rejected sign-in.
  // The server could not be asked who we are. Shown instead of silently
  // bouncing to the login screen, which is what used to happen and made a
  // server-side fault indistinguishable from a rejected sign-in.
  //
  // Outside AppShell on purpose: nobody is signed in at this point, so there
  // are no modules to offer and a nav would be a lie.
  if (status === "error") {
    return (
      <Center mih="100dvh" p="lg" bg="gray.0">
        <Card withBorder radius="md" padding="xl" w="100%" maw={420}>
          <Stack gap="md">
            <Title order={4}>Something is wrong at our end</Title>
            <Alert
              variant="light"
              color="red"
              radius="md"
              icon={<IconAlertTriangle size={18} stroke={1.8} />}
            >
              {errorMessage}
            </Alert>
            <Text fz="md" c="gray.7">
              Your sign-in is probably fine — the server could not answer. Try
              again in a moment. If it persists, check <Code>/api/healthz</Code>,
              which reports whether the database is reachable and the schema is
              up to date.
            </Text>
            <Button size="md" onClick={() => void refresh()}>
              Try again
            </Button>
            <Button variant="subtle" color="gray" onClick={signOut}>
              Sign out
            </Button>
          </Stack>
        </Card>
      </Center>
    );
  }

  if (status === "signedOut") return <Login />;

  // Takes precedence over every route. The server independently refuses all
  // other endpoints while this is outstanding, so this is only making the
  // block visible rather than being the block itself.
  if (mustChangePassword) return <ChangePassword />;

  return (
    // Three layers, and the nesting order is deliberate in both places.
    //
    // MyWorkProvider is outermost because the shell's nav badges read the
    // same counts Home draws as rows. One request, one snapshot, so a badge
    // and a row can never disagree.
    //
    // The boundary wraps the routes rather than each screen: a render fault
    // in any one of them should cost that screen, not the whole session. And
    // AppShell is OUTSIDE it, which is the whole point - a boundary replaces
    // everything below it, so a shell inside one would vanish along with the
    // screen that threw, leaving a dead end with no way out but the browser's
    // back button. Outside, the nav survives and the person can simply go
    // somewhere else.
    <MyWorkProvider>
      <AppShell>
        <ErrorBoundary>
      <Routes>
        <Route path="/" element={<Home />} />
        {/* Reachable by every role; the password section inside it appears only
            for the accounts that have a password. */}
        <Route path="/profile" element={<Profile />} />
        <Route
          path="/marketing"
          element={
            <Guarded module="MARKETING">
              <MarketingHome />
            </Guarded>
          }
        />
        <Route
          path="/marketing/pre-order"
          element={
            <Guarded module="MARKETING">
              <PreOrderPlace />
            </Guarded>
          }
        />
        <Route
          path="/marketing/pre-orders"
          element={
            <Guarded module="MARKETING">
              <PreOrderList />
            </Guarded>
          }
        />
        <Route
          path="/collections"
          element={
            <Guarded module="COLLECTIONS">
              <CollectionsHome />
            </Guarded>
          }
        />
        <Route
          path="/collections/outstanding"
          element={
            <Guarded module="COLLECTIONS">
              <OutstandingBills />
            </Guarded>
          }
        />
        {/* Untouched, and still reached from Outstanding, which is how it is
            actually used: every row there links into this form for that bill.
            Its tile came off the Collections landing - a second door into one
            screen - not its route, which is bookmarked and deep-linked. */}
        <Route
          path="/collections/collect"
          element={
            <Guarded module="COLLECTIONS">
              <PaymentCollect />
            </Guarded>
          }
        />
        {/* A payment for a bill this application never raised. */}
        <Route
          path="/collections/record"
          element={
            <Guarded module="COLLECTIONS">
              <RecordPayment />
            </Guarded>
          }
        />
        {/* The register. Gated on the module and SCOPED by the server - a
            Store Manager sees every receipt, anybody else sees their own - so
            there is nothing to gate here beyond the module. */}
        <Route
          path="/collections/payments"
          element={
            <Guarded module="COLLECTIONS">
              <PaymentsRegister />
            </Guarded>
          }
        />
        <Route
          path="/customers"
          element={
            <RoleGuarded roles={["STORE_MANAGER", "ADMIN"]}>
              <CustomerAdmin />
            </RoleGuarded>
          }
        />
        {/* Declared after "/customers" but they do not collide - these are
            distinct literal paths, not a param. */}
        <Route
          path="/customers/missing-phone"
          element={
            <RoleGuarded roles={["STORE_MANAGER", "ADMIN"]}>
              <MissingPhones />
            </RoleGuarded>
          }
        />
        {/* The product master, alongside the customer master. Role-gated for
            the same reason: these are the lists every other module reads
            from, and field staff select from them rather than adding to
            them. */}
        <Route
          path="/products"
          element={
            <RoleGuarded roles={["STORE_MANAGER", "ADMIN"]}>
              <ProductAdmin />
            </RoleGuarded>
          }
        />
        <Route
          path="/reports"
          element={
            <RoleGuarded roles={["STORE_MANAGER", "ADMIN"]}>
              <Reports />
            </RoleGuarded>
          }
        />
        {/* Q7: the Store Manager is the only one who may change a price, so this
            is role-gated rather than module-gated. */}
        <Route
          path="/prices"
          element={
            <RoleGuarded roles={["STORE_MANAGER", "ADMIN"]}>
              <PriceList />
            </RoleGuarded>
          }
        />
        <Route
          path="/sales"
          element={
            <Guarded module="SALES">
              <SalesHome />
            </Guarded>
          }
        />
        <Route
          path="/sales/queue"
          element={
            <Guarded module="SALES">
              <PreOrderQueue />
            </Guarded>
          }
        />
        <Route
          path="/sales/confirm/:id"
          element={
            <Guarded module="SALES">
              <OrderConfirm />
            </Guarded>
          }
        />
        <Route
          path="/sales/orders"
          element={
            <Guarded module="SALES">
              <OrderList />
            </Guarded>
          }
        />
        {/* Billing and viewing a bill are the same screen, keyed on the ORDER:
            an order that is already billed shows its bill instead of the form,
            so a stale link can never produce a second bill. */}
        <Route
          path="/sales/bill/:orderId"
          element={
            <Guarded module="SALES">
              <BillGenerate />
            </Guarded>
          }
        />
        <Route
          path="/sales/bills"
          element={
            <Guarded module="SALES">
              <BillList />
            </Guarded>
          }
        />
        <Route
          path="/service"
          element={
            <Guarded module="SERVICE">
              <ServiceHome />
            </Guarded>
          }
        />
        <Route
          path="/service/collect"
          /* Role-guarded, not module-guarded: only an outdoor technician may
             collect, so anybody else landing here is bounced rather than
             filling in a form the API will refuse. */
          element={
            <RoleGuarded roles={["OUTDOOR_SERVICE_TECHNICIAN"]}>
              <CollectBattery />
            </RoleGuarded>
          }
        />
        {/* Three lists, one component - the path picks the scope. See
            ServiceQueue. */}
        <Route
          path="/service/queue"
          element={
            <Guarded module="SERVICE">
              <ServiceQueue />
            </Guarded>
          }
        />
        <Route
          path="/service/delivery"
          element={
            <Guarded module="SERVICE">
              <ServiceQueue />
            </Guarded>
          }
        />
        <Route
          path="/service/mine"
          element={
            <Guarded module="SERVICE">
              <ServiceQueue />
            </Guarded>
          }
        />
        <Route
          path="/service/jobs"
          element={
            <Guarded module="SERVICE">
              <ServiceQueue />
            </Guarded>
          }
        />
        <Route
          path="/service/jobs/:id"
          element={
            <Guarded module="SERVICE">
              <ServiceJobDetail />
            </Guarded>
          }
        />
        <Route
          path="/delivery"
          element={
            <Guarded module="DELIVERY">
              <DeliveryHome />
            </Guarded>
          }
        />
        {/* Three lists, one component - the path picks the scope. See
            DeliveryQueue. */}
        {/* MOVED into Service. The business assigns service deliveries through
            the Service Manager, so the queue lives where they work.

            A REDIRECT rather than a deletion: the Store Manager has used this
            path daily and will have it bookmarked, and they keep access. That
            is the difference from /marketing/collect, which was deliberately
            left to die - there the screen changed MODULE and the people who
            used it lost the right to it, so a working bookmark would have
            been a lie. Here it is the same screen and the same people. */}
        <Route
          path="/delivery/queue"
          element={<Navigate to="/service/assign-deliveries" replace />}
        />
        {/* Assigning a delivery is the Service Manager's job now, so the
            queue is a SERVICE route - and gated by ROLE, not by module.

            A Service Manager holds SERVICE only. Granting them DELIVERY to
            reach this one screen would also hand them the Delivery List, the task
            detail and the handover form, which is a great deal more than
            "choose who carries this". The Store Manager reaches it from here
            too, and keeps everything else in Delivery unchanged. */}
        <Route
          path="/service/assign-deliveries"
          element={
            <RoleGuarded roles={["SERVICE_MANAGER", "STORE_MANAGER", "ADMIN"]}>
              <DeliveryQueue />
            </RoleGuarded>
          }
        />
        <Route
          path="/delivery/round"
          element={
            <Guarded module="DELIVERY">
              <DeliveryQueue />
            </Guarded>
          }
        />
        <Route
          path="/delivery/all"
          element={
            <Guarded module="DELIVERY">
              <DeliveryQueue />
            </Guarded>
          }
        />
        <Route
          path="/delivery/tasks/:id"
          element={
            <Guarded module="DELIVERY">
              <DeliveryTaskDetail />
            </Guarded>
          }
        />
        <Route
          path="/inventory"
          element={
            <Guarded module="INVENTORY">
              <InventoryHome />
            </Guarded>
          }
        />
        <Route
          path="/inventory/levels"
          element={
            <Guarded module="INVENTORY">
              <StockLevels />
            </Guarded>
          }
        />
        <Route
          path="/inventory/stock-in"
          element={
            <Guarded module="INVENTORY">
              <StockIn />
            </Guarded>
          }
        />
        {/* One screen for the whole ledger and for a single product's history;
            ?productId= narrows it, so "History" on a stock row is a link
            rather than a second screen. */}
        <Route
          path="/inventory/movements"
          element={
            <Guarded module="INVENTORY">
              <MovementLedger />
            </Guarded>
          }
        />
        <Route
          path="/inventory/reconcile"
          element={
            <Guarded module="INVENTORY">
              <Reconciliation />
            </Guarded>
          }
        />
        <Route
          path="/admin"
          element={
            <Guarded module="ADMIN">
              <AdminHome />
            </Guarded>
          }
        />
        <Route
          path="/admin/users"
          element={
            <Guarded module="ADMIN">
              <UserList />
            </Guarded>
          }
        />
        {/* "new" before ":id" is unnecessary with one component serving both,
            but the component still needs to tell them apart - it treats the
            literal "new" as create. */}
        <Route
          path="/admin/users/:id"
          element={
            <Guarded module="ADMIN">
              <UserForm />
            </Guarded>
          }
        />
        <Route
          path="/admin/audit"
          element={
            <Guarded module="ADMIN">
              <AuditLog />
            </Guarded>
          }
        />
        {/* The shared-primitive gallery. Not part of the product: it is how
            the UI retrofit got reviewed before any screen changed.
            
            It was going to be deleted with the legacy stylesheet, and is
            kept instead. It is the only place the whole design vocabulary can
            be seen at once, which is what makes it useful for the NEXT
            change rather than only the last one - and it costs nothing, being
            a lazy chunk nobody fetches by accident.
            
            Signed-in only (everything here is behind the auth gate above) and
            it reads no data, so there is nothing to guard beyond that. */}
        <Route
          path="/dev/kit"
          element={
            <Suspense fallback={<Loader aria-label="Loading" m="xl" />}>
              <DevKit />
            </Suspense>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
        </ErrorBoundary>
      </AppShell>
    </MyWorkProvider>
  );
}
